import * as React from "react";
import { Trash2 } from "lucide-react";
import { ShareDialog } from "@/components/share/ShareDialog";
import type { ShareAccess, ShareRole } from "@/components/share/types";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useCreatePermissionMutation, useDeletePermissionMutation, useUpdateFileAccessMutation } from "@/api/files";
import { type FsEntryPermissionEntry, Item } from "@/types/fse";

interface Props {
  item: Item;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}

export const ShareModal: React.FC<Props> = ({ isOpen, setIsOpen, item }) => {
  const authenticatedUser = useAuthenticatedUser();
  const [createPermission] = useCreatePermissionMutation();
  const [deletePermission] = useDeletePermissionMutation();
  const [updateFileAccess] = useUpdateFileAccessMutation();
  const { id: fseId, owner_id: ownerId } = item;

  const isReadOnly = !!item.permissions.some(row => row.user_id === authenticatedUser.id && row.role === "viewer");

  const handleShare = React.useCallback(
    async (users: string[], role: ShareRole) => {
      await createPermission({ fse_id: fseId, role, users }).unwrap();
    },
    [createPermission, fseId],
  );

  const handleRemovePermission = React.useCallback(
    async (permission: FsEntryPermissionEntry) => {
      await deletePermission({ fse_id: permission.fse_id, permission_id: permission.id }).unwrap();
    },
    [deletePermission],
  );

  const handleAccessChange = React.useCallback(
    async (access: ShareAccess) => {
      await updateFileAccess({ fse_id: fseId, access }).unwrap();
    },
    [updateFileAccess, fseId],
  );

  return (
    <ShareDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <>
          Share {item.type} &#34;{item.name}&#34;
        </>
      }
      isReadOnly={isReadOnly}
      permissions={item.permissions}
      access={item.access}
      shareSuccessMessage="Permissions has been created successfully."
      onShare={handleShare}
      onRemovePermission={handleRemovePermission}
      canRemovePermission={permission => permission.user_id !== authenticatedUser.id && permission.user_id !== ownerId}
      onAccessChange={handleAccessChange}
      RemoveIcon={Trash2}
    />
  );
};
